import { Calendar, DollarSign, MapPin, Users, XIcon } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { Button, Skeleton, SmallBadge } from '@/Components/shadcn';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import UIStrings from '@/Utils/UIStrings';
import { useCompanyLookup } from '../../api/useCompanyLookup';
import CompanyLogo from '../CompanyLogo/CompanyLogo';
import CompanyDetailPointList from './CompanyDetailPointList';
import { cn } from '@/lib/utils';

export interface CompanyDetailProps {
    companyDomain: string | null;
    /** used as fallback */
    companyName: string;
    companyLinkedInUrl: string | null;
    onCloseIconClick?: () => void;
}

const CompanyDetail = ({
    companyDomain,
    companyName: companyNameFallback,
    companyLinkedInUrl,
    onCloseIconClick,
}: CompanyDetailProps) => {
    const { data, isLoading, error } = useCompanyLookup(companyNameFallback, companyDomain, companyLinkedInUrl);
    const [overflowWidth, setOverflowWidth] = useState(0);
    const componentWidth = useMemo(() => {
        const baseWidth = 386;

        return `${baseWidth + overflowWidth}px`;
    }, [overflowWidth]);
    const companyName = data?.name || companyNameFallback;
    const targetRef = useCallback(
        (target: HTMLSpanElement) => {
            if (!target || !data?.industry) {
                return;
            }

            const observer = new ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    const overflownWidth = entry.target.scrollWidth - entry.target.clientWidth;
                    if (overflownWidth > 0) {
                        setOverflowWidth(overflownWidth);
                    }
                });
            });

            observer.observe(target);
        },
        [setOverflowWidth, data?.industry],
    );

    return (
        <div
            data-cy="companyDetailContainer"
            className="flex w-full flex-col gap-4 p-5"
            style={{ width: componentWidth }}
        >
            <div className="flex w-full gap-4">
                <div className="self-center">
                    <CompanyLogo companyDomain={companyDomain} companyName={companyName} size="40px" />
                </div>
                <div className="flex w-full flex-col justify-center gap-1 overflow-hidden">
                    {isLoading ? (
                        <Skeleton className="h-7 w-48" />
                    ) : (
                        <a
                            href={data?.website!}
                            target="_blank"
                            rel="noreferrer"
                            className={cn('text-foreground w-fit', data?.website ? 'hover:underline' : '')}
                        >
                            <h3 className="text-xl font-medium">{companyName}</h3>
                        </a>
                    )}
                    {isLoading ? (
                        <Skeleton className="h-4 w-32" />
                    ) : (
                        data?.type && <p className="text-muted-foreground text-sm">{data.type}</p>
                    )}
                    {isLoading ? (
                        <Skeleton className="h-5 w-24" />
                    ) : (
                        data?.industry && (
                            <span ref={targetRef} className="w-auto overflow-hidden whitespace-nowrap">
                                <SmallBadge variant="default" className="w-max">
                                    {data.industry}
                                </SmallBadge>
                            </span>
                        )
                    )}
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="close button"
                    className="ml-auto"
                    onClick={onCloseIconClick}
                >
                    <XIcon />
                </Button>
            </div>
            {error instanceof ResourceNotFoundError ? (
                <p className="text-xs">{UIStrings.InformationNotAvailable}</p>
            ) : (
                <>
                    {isLoading ? (
                        <div className="flex flex-col gap-2">
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-3 w-4/5" />
                        </div>
                    ) : (
                        data?.description && <p className="text-xs leading-relaxed">{data.description}</p>
                    )}
                    <div className="grid grid-cols-2 gap-x-2.5 gap-y-3">
                        <CompanyDetailPointList
                            icon={<DollarSign className="size-4 shrink-0" />}
                            label={UIStrings.Revenue}
                            value={data?.revenueTextFormatted}
                            isLoading={isLoading}
                        />
                        <CompanyDetailPointList
                            icon={<Users className="size-4 shrink-0" />}
                            label={UIStrings.Employees}
                            value={data?.employeesCount}
                            isLoading={isLoading}
                        />
                        <CompanyDetailPointList
                            icon={<Calendar className="size-4 shrink-0" />}
                            label={UIStrings.Founded}
                            value={data?.foundedAt}
                            isLoading={isLoading}
                        />
                        <CompanyDetailPointList
                            icon={<MapPin className="size-4 shrink-0" />}
                            label={UIStrings.HQ}
                            value={data?.headquarter}
                            isLoading={isLoading}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default CompanyDetail;
